import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { Provider } from '@/types/core/provider';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Hook to update a provider.
 * Returns a react-query mutation that accepts partial provider updates.
 * Invalidates the providers cache to trigger a refetch.
 */
export function useUpdateProvider(tenantId?: UUID, providerId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.updateProvider(tenantId, providerId),
    mutationFn: async (updates: { name?: string; description?: string }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!providerId) throw new Error('Missing provider id');

      const cachedProvider = qc.getQueryData<Provider | null>(
        providerQueryKeys.detail(tenantId, providerId)
      );
      const minRev = cachedProvider?._rev ?? -2;

      await providerService.updateProvider(tenantId, providerId, updates);

      return pollUntilResourceNewerThan(
        () => providerService.findProvider(tenantId, providerId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: providerQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
